import { delay, put, select, takeEvery } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';

import { INewsRequest } from 'APIPackage/NewsModule/interfaces/INewsRequest';
import { addNews, attachmentFile, deleteFile, getSingleNews, updateNews } from 'APIPackage/NewsModule/services/newsApi';
import { NewsTypeEnum } from 'APIPackage/NewsModule/enums/NewsTypeEnum';
import { INTERNAL_SERVER_ERROR } from 'CorePackage/MainModule/constants/httpErrors';
import {
  ANNOUNCE_CREATED_SUCCESSFULLY,
  ANNOUNCE_UPDATED_SUCCESSFULLY,
  NEWS_CREATED_SUCCESSFULLY,
  NEWS_UPDATED_SUCCESSFULLY,
} from 'CorePackage/MainModule/constants/notices';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { rolesDict } from 'NewsPackage/MainModule/constants/rolesDict';
import {
  DATE_FORMAT_ISO_FNS,
  DATE_FORMAT_LONG_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
} from 'CorePackage/MainModule/constants/dateFormats';
import { INews } from 'APIPackage/NewsModule/interfaces/INews';
import { ROUTE_ADMIN_NEWS } from 'CorePackage/MainModule/routes/routes';
import { compareDates, formatDate } from 'CorePackage/MainModule/common/formatDate';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';

import {
  newsFormAdd,
  newsFormGetSingleNews,
  newsFormSetNewsId,
  newsFormSetSingleNews,
  newsFormUpdate,
} from '../actions/newsFormActions';
import { NewsFormActionsEnum } from '../enums/NewsFormActionsEnum';
import { mapNewsType } from '../common/mapNewsType';
import { INewsForm } from '../interfaces/INewsForm';
import { initialNewsForm } from '../constants/initialNewsForm';
import { getNewsFormNewsId } from '../selectors/newsFormSelectors';
import { isNewsHtmlText } from '../../MainModule/common/isNewsHtmlText';

const NEWS_FORM_DELAY = 1000;

export function* watchNewsFormSaga() {
  yield takeEvery(NewsFormActionsEnum.NEWS_FORM_ADD, newsFormAddSaga);
  yield takeEvery(NewsFormActionsEnum.NEWS_FORM_GET_SINGLE_NEWS, newsFormGetSingleNewsSaga);
  yield takeEvery(NewsFormActionsEnum.NEWS_FORM_UPDATE, newsFormUpdateSaga);
}

function* newsFormAddSaga(action: ActionType<typeof newsFormAdd>) {
  function* funcTry() {
    const { values, files, history } = action.payload;
    const newsFormId: string | undefined = yield select(getNewsFormNewsId);
    const news: INewsRequest = {
      type: values.type?.value,
      title: values.title,
      shortText: values.shortText,
      text: values.text,
      expired: values.expired
        ? formatDate({
            date: values.expired,
            dateFormat: DATE_FORMAT_ISO_FNS,
          })
        : '',
      published: values.published
        ? formatDate({
            date: values.published,
            dateFormat: DATE_FORMAT_ISO_FNS,
          })
        : '',
      roles: values.roles?.map(x => +x.id) || [],
      countdownStop: values.countdownStop
        ? formatDate({
            date: values.countdownStop,
            parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
            dateFormat: DATE_FORMAT_ISO_FNS,
          })
        : '',
    };
    const response: INews = yield addNews(news);

    yield put(
      addNoticeAction({
        renderContent:
          values.type?.id === NewsTypeEnum.NEWS ? NEWS_CREATED_SUCCESSFULLY : ANNOUNCE_CREATED_SUCCESSFULLY,
        noticeType: NoticeTypesEnum.SUCCESS,
        messageType: MessageTypesEnum.CREATE_NEWS,
      }),
    );
    if (response && files && files.length !== 0) {
      yield files.forEach(file => {
        attachmentFile(response.id, file.data);
      });
    }
    if (history) {
      history.push(ROUTE_ADMIN_NEWS);
    }
    yield put(newsFormGetSingleNews(newsFormId));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: INTERNAL_SERVER_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.INTERNAL_SERVER,
        }),
      );
    },
  });
}

function* newsFormGetSingleNewsSaga(action: ActionType<typeof newsFormGetSingleNews>) {
  function* funcTry() {
    const { newsFormId } = action.payload;

    if (newsFormId) {
      const data: INews = yield getSingleNews(newsFormId);
      const news: INewsForm = {
        ...data,
        type: {
          id: data.type,
          label: mapNewsType(data.type),
          value: data.type,
        },
        roles: data.roles.map(x => rolesDict[x]),
        published: data.published
          ? formatDate({
              date: data.published,
              dateFormat: DATE_FORMAT_LONG_FNS,
            })
          : '',
        expired: data.expired
          ? formatDate({
              date: data.expired,
              dateFormat: DATE_FORMAT_LONG_FNS,
            })
          : '',
        countdownStop: data.countdownStop
          ? formatDate({
              date: data.countdownStop,
              dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
              parseFormat: DATE_FORMAT_ISO_FNS,
            })
          : '',
        stripHtmlTags: data.type === NewsTypeEnum.ANNOUNCEMENT ? !isNewsHtmlText(data.text) : undefined,
      };

      yield put(newsFormSetSingleNews(news));
      yield put(newsFormSetNewsId(newsFormId));
    } else {
      yield put(newsFormSetNewsId());
      yield put(newsFormSetSingleNews(initialNewsForm));
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* newsFormUpdateSaga(action: ActionType<typeof newsFormUpdate>) {
  function* funcTry() {
    const { values, deletedFiles, files } = action.payload;
    const newsFormId: string | undefined = yield select(getNewsFormNewsId);

    if (newsFormId) {
      const compareResult = compareDates({ dateLeft: values.published, dateRight: values.expired });
      const news: INewsRequest = {
        type: values.type?.value,
        text: values.text,
        shortText: values.shortText,
        title: values.title,
        roles: values.roles?.map(x => +x.id) || [],
        published: values.published
          ? formatDate({
              date: values.published,
              dateFormat: DATE_FORMAT_ISO_FNS,
            })
          : '',
        expired:
          values.expired && (compareResult === -1 || compareResult === 0)
            ? formatDate({ date: values.expired, dateFormat: DATE_FORMAT_ISO_FNS })
            : '',
        countdownStop: values.countdownStop
          ? formatDate({
              date: values.countdownStop,
              parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
              dateFormat: DATE_FORMAT_ISO_FNS,
            })
          : '',
      };

      yield updateNews(newsFormId, news);
      if (deletedFiles?.length) {
        yield deletedFiles.forEach(id => {
          deleteFile(newsFormId, id);
        });
      }
      if (files?.length) {
        yield files.forEach(file => {
          attachmentFile(newsFormId, file.data);
        });
      }
      yield delay(NEWS_FORM_DELAY);
      yield put(newsFormGetSingleNews(newsFormId));
      yield put(
        addNoticeAction({
          renderContent:
            values.type?.id === NewsTypeEnum.NEWS ? NEWS_UPDATED_SUCCESSFULLY : ANNOUNCE_UPDATED_SUCCESSFULLY,
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.UPDATE_NEWS,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: INTERNAL_SERVER_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.INTERNAL_SERVER,
        }),
      );
    },
  });
}
